import { FC } from 'react';
import { ImageModelType } from 'types';

import {
  GPT_IMAGE_2_ASPECTS,
  GPT_IMAGE_2_EDIT_ASPECTS,
  isGptImage2FamilyModel,
} from '@/constants/gptImageModels';

import BriaSvg from '~/svg/bria.svg';
import ByteDanceSvg from '~/svg/bytedance.svg';
import FluxSvg from '~/svg/flux.svg';
import GoogleSvg from '~/svg/google_small.svg';
import HailuoSvg from '~/svg/Hailuo.svg';
import HunyuanSvg from '~/svg/hunyuan.svg';
import IdeogramSvg from '~/svg/ideogram.svg';
import ImagineArtSvg from '~/svg/ImagineArt.svg';
import MetaSvg from '~/svg/meta.svg';
import MicrosoftSvg from '~/svg/microsoft.svg';
import MidjourneySvg from '~/svg/midjourney.svg';
import OpenAISvg from '~/svg/OpenAI.svg';
import QwenSvg from '~/svg/qwen.svg';
import RecraftSvg from '~/svg/recraft.svg';
import ReveSvg from '~/svg/reve.svg';
import StableAISvg from '~/svg/stableai.svg';
import WanSvg from '~/svg/wan.svg';
import XAISvg from '~/svg/xai.svg';

export const models: ImageModelType[] = [
  'Nano Banana 2.1',
  'Nano Banana 2 Lite',
  'Nano Banana 2',
  'OpenAI GPT Image 2 Low',
  'OpenAI GPT Image 2 Medium',
  'OpenAI GPT Image 2 High',
  'OpenAI GPT Image 2.5 Flare Low',
  'OpenAI GPT Image 2.5 Flare Medium',
  'OpenAI GPT Image 2.5 Flare High',
  'OpenAI GPT Image 2.5 Sunburst Medium',
  'OpenAI GPT Image 2.5 Sunburst High',
  'Nano Banana Pro',
  'Nano Banana Flash',
  'Z-Image Turbo',
  'ImagineArt 1.5',
  'Mai Image 2.5',
  'Muse Image',
  'FLUX.2 [max]',
  'FLUX.2 [flex]',
  'FLUX.2 [pro]',
  'FLUX.2 [dev]',
  'Seedream 4.0',
  'Seedream 4.5',
  'Seedream 5.0 Lite',
  'Seedream 5.0 Pro',
  'Qwen Image Max',
  'Qwen Image 2.0',
  'WAN 2.7 Pro',
  'WAN 2.7',
  'Krea v2 Large',
  'Krea v2 Medium',
  'Krea v2 Medium Turbo',
  'Luma Uni v1',
  'Boogu Image',
  'Cosmos 3 Super',
  'P-Image',
  'HunyuanImage 3.0',
  'REVE',
  'Recraft v4.1',
  'Recraft v4.1 Pro',
  'Recraft v4.1 Vector',
  'Recraft v4.1 Pro Vector',
  'Recraft v3',
  'Stable Diffusion XL',
  'FLUX.1',
  'Stable Diffusion 3.5',
  'Bria 3.2',
  'HiDream',
  'WAN 2.2',
  'WAN 2.5 Preview',
  'Dreamina 3.1',
  'Qwen-Image',
  'Grok Imagine 2.0',
  'Grok',
  'Grok Pro',
  'Kandinsky 3.0',
  'Kandinsky 2.2',
  'Kandinsky 2',
  'MiniMax Image 01',
  'FLUX 1.1 Pro Ultra',
  'Ideogram v4',
  'Ideogram v3',
  'Flux Kontext Pro',
  'Flux Kontext Max',
  'Seedream 3.0',
  'Nano Banana',
];

interface ModelWithDescription {
  id: string;
  title: string;
  description: string;
  users: string;
  tags?: string[];
  icon?: FC<React.SVGProps<SVGSVGElement>>;
  badge?: string;
  isCustom?: boolean;
  resolutions?: string[];
}

export const modelsWithDescription: ModelWithDescription[] = [
  {
    id: 'nano-banana-2-1',
    title: 'Nano Banana 2.1',
    description:
      "Google's newest Nano Banana: better visual design, text rendering and character consistency than Nano Banana 2, more natural-looking images, at Flash speed and half the price.",
    users: '2 credits (1K), 3 credits (2K), 6 credits (4K)',
    tags: ['New', '4K'],
    icon: GoogleSvg,
    resolutions: ['1K', '2K', '4K'],
  },
  {
    id: 'nano-banana-2-lite',
    title: 'Nano Banana 2 Lite',
    description:
      "Google's Gemini 3.1 Flash-Lite Image model — the fastest, most cost-efficient Nano Banana for high-quality image generation and editing at ~4s per image.",
    users: '2 credits per image',
    tags: ['Fast', 'New'],
    icon: GoogleSvg,
    resolutions: ['1K'],
  },
  {
    id: 'nano-banana-2',
    title: 'Nano Banana 2',
    description:
      "Google's Gemini 3.1 Flash Image model for general image generation and editing with high quality and low latency.",
    users: '3 credits (0.5K), 4 credits (1K), 6 credits (2K), 8 credits (4K)',
    tags: ['Fast', '4K'],
    icon: GoogleSvg,
    resolutions: ['0.5K', '1K', '2K', '4K'],
  },
  {
    id: 'nano-banana-pro',
    title: 'Nano Banana Pro',
    description:
      "Google's Gemini 3 Pro model for advanced image generation with superior quality and detail.",
    users: '7 credits (1K/2K), 12 credits (4K)',
    tags: ['2K', '4K'],
    icon: GoogleSvg,
    resolutions: ['1K', '2K', '4K'],
  },
  {
    id: 'muse-image',
    title: 'Muse Image',
    description:
      "Meta's Muse Image — an agentic image model that can look up visual references and current facts before it renders, with strong text, infographic and product accuracy. Generates and edits.",
    users: '1 credit',
    tags: ['New'],
    icon: MetaSvg,
  },
  {
    id: 'grok-imagine-2-0',
    title: 'Grok Imagine 2.0',
    description:
      "xAI's latest Grok Imagine 2.0 model with improved photorealism, prompt adherence, aspect ratio control, and image editing support.",
    users: '3 credits (1K), 4 credits (2K)',
    tags: ['New', '2K'],
    icon: XAISvg,
    resolutions: ['1K', '2K'],
  },
  {
    id: 'grok',
    title: 'Grok',
    description:
      "xAI's Grok image generation model with unique stylistic qualities, aspect ratio control, and image editing support.",
    users: '1 credit',
    tags: ['2K'],
    icon: XAISvg,
    resolutions: ['1K', '2K'],
  },
  {
    id: 'grok-pro',
    title: 'Grok Pro',
    description:
      "xAI's premium Grok image generation model with superior quality, aspect ratio control, and image editing support.",
    users: '4 credits',
    tags: ['2K'],
    icon: XAISvg,
    resolutions: ['1K', '2K'],
  },
  // {
  //   id: 'nano-banana-flash',
  //   title: 'Nano Banana Flash',
  //   description:
  //     "Google's Gemini 3 Flash model for fast image generation with excellent quality.",
  //   users: '1 credit (2K), 4 credits (4K)',
  //   tags: ['Fast', '2K', '4K'],
  //   icon: GoogleSvg,
  //   resolutions: ['1K', '2K', '4K'],
  // },
  {
    id: 'gpt-image-2-low',
    title: 'OpenAI GPT Image 2 Low',
    icon: OpenAISvg,
    description:
      "OpenAI's GPT Image 2 at low quality — fastest and cheapest, great for drafts, thumbnails, and quick iterations.",
    users: '0.5 credits per image',
    tags: ['Fast'],
  },
  {
    id: 'gpt-image-2-medium',
    title: 'OpenAI GPT Image 2 Medium',
    icon: OpenAISvg,
    description:
      "OpenAI's GPT Image 2 at medium quality — a balanced option for most production use cases.",
    users: '3 credits per image',
  },
  {
    id: 'gpt-image-2-high',
    title: 'OpenAI GPT Image 2 High',
    icon: OpenAISvg,
    description:
      "OpenAI's GPT Image 2 model at high quality for maximum detail, sharpest text rendering, and best prompt adherence.",
    users: '11 credits per image',
    tags: ['2K'],
  },
  {
    id: 'gpt-image-2.5-flare-low',
    title: 'OpenAI GPT Image 2.5 Flare Low',
    icon: OpenAISvg,
    description:
      "OpenAI's GPT Image 2.5 Flare at low quality — up to 50% faster than GPT Image 2, ideal for drafts, thumbnails, and rapid iteration.",
    users: '0.5 credits per image',
    tags: ['Fast', 'New'],
  },
  {
    id: 'gpt-image-2.5-flare-medium',
    title: 'OpenAI GPT Image 2.5 Flare Medium',
    icon: OpenAISvg,
    description:
      "OpenAI's GPT Image 2.5 Flare at medium quality — OpenAI's recommended default: more natural lighting, richer textures, better reference-photo preservation, and up to 50% lower latency than GPT Image 2.",
    users: '3 credits per image',
    tags: ['New'],
  },
  {
    id: 'gpt-image-2.5-flare-high',
    title: 'OpenAI GPT Image 2.5 Flare High',
    icon: OpenAISvg,
    description:
      "OpenAI's GPT Image 2.5 Flare at high quality — maximum detail and the sharpest text rendering, still up to 50% faster than GPT Image 2 High.",
    users: '11 credits per image',
    tags: ['New', '2K'],
  },
  {
    id: 'gpt-image-2.5-sunburst-medium',
    title: 'OpenAI GPT Image 2.5 Sunburst Medium',
    icon: OpenAISvg,
    description:
      "OpenAI's GPT Image 2.5 Sunburst at medium quality — the premium tier for tighter control across multi-step edits, campaign creative, and polished product imagery (slower than Flare).",
    users: '3 credits per image',
    tags: ['New'],
  },
  {
    id: 'gpt-image-2.5-sunburst-high',
    title: 'OpenAI GPT Image 2.5 Sunburst High',
    icon: OpenAISvg,
    description:
      "OpenAI's GPT Image 2.5 Sunburst at high quality — production-ready campaign assets and product shots with the most precise editing control OpenAI offers.",
    users: '11 credits per image',
    tags: ['New', '2K'],
  },
  {
    id: 'gpt-image-1.5',
    title: 'OpenAI GPT Image 1.5',
    icon: OpenAISvg,
    description:
      'Most advanced OpenAI image model with improved image preservation, better instruction following, enhanced text rendering, and 20% lower cost.',
    users: '2 credits per image (standard), 8 credits (high quality)',
  },
  {
    id: 'seedream-4.5',
    title: 'Seedream 4.5',
    description:
      "ByteDance's new-generation image creation model with superior quality and prompt adherence.",
    users: '2 credits per image',
    tags: ['4K'],
    resolutions: ['1K', '2K', '4K'],
    icon: ByteDanceSvg,
  },
  {
    id: 'seedream-5.0-lite',
    title: 'Seedream 5.0 Lite',
    description:
      "ByteDance's fast Seedream 5.0 Lite model with high quality intelligent text-to-image generation.",
    users: '2 credits per image',
    tags: ['3K'],
    resolutions: ['1K', '2K', '3K'],
    icon: ByteDanceSvg,
  },
  {
    id: 'seedream-5.0-pro',
    title: 'Seedream 5.0 Pro',
    description:
      "ByteDance's flagship Seedream 5.0 Pro model with deep-thinking prompt understanding, native text in 14 languages, and precise control over dense layouts.",
    users: '4 credits (1K), 8 credits (2K)',
    tags: ['New', '2K'],
    resolutions: ['1K', '2K'],
    icon: ByteDanceSvg,
  },
  {
    id: 'qwen-image-max',
    title: 'Qwen Image Max',
    description:
      "Alibaba's flagship Qwen image model with superior prompt understanding, dense text rendering, and rich detail.",
    users: '4 credits per image',
    tags: ['New'],
    icon: QwenSvg,
  },
  {
    id: 'qwen-image-2.0',
    title: 'Qwen Image 2.0',
    description:
      "Alibaba's new-generation Qwen image model with excellent prompt following in Chinese and English and strong text rendering.",
    users: '2 credits per image',
    tags: ['New', '2K'],
    icon: QwenSvg,
  },
  {
    id: 'wan-2.7-pro',
    title: 'WAN 2.7 Pro',
    description:
      "Alibaba's premium WAN 2.7 image model with deep prompt reasoning for the highest quality photorealistic output.",
    users: '4 credits per image',
    tags: ['New'],
    icon: WanSvg,
  },
  {
    id: 'wan-2.7',
    title: 'WAN 2.7',
    description:
      "Alibaba's WAN 2.7 image model with prompt reasoning, ultra-realistic textures, and excellent prompt adherence.",
    users: '2 credits per image',
    tags: ['New', '2K'],
    icon: WanSvg,
  },
  {
    id: 'krea-v2-large',
    title: 'Krea v2 Large',
    description:
      "Krea's largest v2 model with distinctive aesthetics and adjustable creativity for striking, non-generic images.",
    users: '3 credits per image',
    tags: ['New'],
  },
  {
    id: 'krea-v2-medium',
    title: 'Krea v2 Medium',
    description:
      "Krea's balanced v2 model with great aesthetics and quality at a mid-tier price.",
    users: '2 credits per image',
    tags: ['New'],
  },
  {
    id: 'krea-v2-medium-turbo',
    title: 'Krea v2 Medium Turbo',
    description:
      "Krea's fastest v2 model — great aesthetics at high speed and low cost.",
    users: '1 credit per image',
    tags: ['New', 'Fast'],
  },
  {
    id: 'luma-uni-v1',
    title: 'Luma Uni v1',
    description:
      "Luma's unified image model with strong composition, lighting, and support for wide and tall aspect ratios.",
    users: '2 credits per image',
    tags: ['New', '2K'],
  },
  {
    id: 'boogu-image',
    title: 'Boogu Image',
    description:
      'High-quality text-to-image generation with strong aesthetics and reliable prompt adherence.',
    users: '3 credits per image',
    tags: ['New'],
  },
  {
    id: 'cosmos-3-super',
    title: 'Cosmos 3 Super',
    description:
      "NVIDIA's Cosmos 3 Super model for physically grounded, photorealistic image generation.",
    users: '2 credits per image',
    tags: ['New'],
  },
  {
    id: 'p-image',
    title: 'P-Image',
    description:
      "Pruna's ultra-efficient image model — solid quality at the lowest cost and very high speed.",
    users: '0.5 credits per image',
    tags: ['New', 'Fast'],
  },
  {
    id: 'z-image-turbo',
    title: 'Z-Image Turbo',
    description:
      'Super fast text-to-image model with excellent quality and speed.',
    users: '0.1 credits (1K), 1 credit (2K)',
    tags: ['Fast', '2K'],
    resolutions: ['1K', '2K'],
    icon: WanSvg,
  },
  {
    id: 'imagineart-1.5',
    title: 'ImagineArt 1.5',
    description:
      'High-fidelity professional-grade visuals with lifelike realism, strong aesthetics, and accurate text rendering.',
    users: '2 credits per image',
    icon: ImagineArtSvg,
  },
  {
    id: 'mai-image-2.5',
    title: 'Mai Image 2.5',
    description:
      "Microsoft's photorealistic model with pixel-level control and strong typography, turning prompts into design-ready visuals.",
    users: '2 credits per image',
    tags: ['New'],
    icon: MicrosoftSvg,
  },
  {
    id: 'flux-2-max',
    title: 'FLUX.2 [max]',
    description:
      'Maximum quality AI image generation with superior detail and prompt adherence. Optimized for the highest quality production assets.',
    users: '2.5 credits (1K), 5 credits (2K)',
    icon: FluxSvg,
    resolutions: ['1K', '2K'],
  },
  {
    id: 'flux-2-flex',
    title: 'FLUX.2 [flex]',
    description:
      'Production-grade AI image generation with 4MP photorealistic output. Highest detail and prompt adherence for final production assets.',
    users: '3 credits (1K), 6 credits (2K)',
    icon: FluxSvg,
    resolutions: ['1K', '2K'],
  },
  {
    id: 'flux-2-pro',
    title: 'FLUX.2 [pro]',
    description:
      'Best balance of speed and cost. Ideal for high-volume applications needing fast turnaround.',
    users: '2 credits per image',
    icon: FluxSvg,
    resolutions: ['1K', '2K'],
  },
  {
    id: 'flux-2-dev',
    title: 'FLUX.2 [dev]',
    description:
      'Enhanced realism, crisper text generation, and native editing capabilities.',
    users: '1 credit per image',
    icon: FluxSvg,
    resolutions: ['1K', '2K'],
  },
  {
    id: 'hunyuan-image-3.0',
    title: 'HunyuanImage 3.0',
    description:
      'Best open-source text-to-image model with complex prompt understanding and text generation within images.',
    users: '5 credits per image',
    icon: HunyuanSvg,
  },
  {
    id: 'nano-banana',
    title: 'Nano Banana',
    description:
      "Google's Gemini 2.5 Flash model for creative image generation with excellent prompt understanding.",
    users: '2 credits per image',
    icon: GoogleSvg,
  },

  {
    id: 'reve',
    title: 'REVE',
    description:
      'Generates detailed visual output that closely follows your instructions, with strong aesthetic quality and accurate text rendering.',
    users: '2 credits per image',
    tags: ['New'],
    icon: ReveSvg,
  },
  {
    id: 'flux-1-krea',
    title: 'Flux.1 Krea',
    description:
      'FLUX.1 Krea is a new model that generates high-quality images from text with incredible aesthetics.',
    users: '2 credits per image',
    icon: FluxSvg,
  },
  {
    id: 'flux-kontext-pro',
    title: 'Flux Kontext Pro',
    icon: FluxSvg,
    description:
      'Optimized for detailed prompts and iterative editing with excellent character consistency; supports reference images.',
    users: '2 credits per image (standard), 4 credits (HD)',
  },
  {
    id: 'flux-kontext-max',
    title: 'Flux Kontext Max',
    description:
      'The most advanced model for high precision and superior prompt adherence; supports reference images, complex scene editing, and flawless typography.',
    users: '4 credits per image',
    icon: FluxSvg,
  },
  {
    id: 'gpt-image-1',
    title: 'OpenAI GPT Image 1',
    icon: OpenAISvg,
    description:
      'New OpenAI model with specific capabilities. Check OpenAI docs for details.',
    users: '2 credits per image (standard), 8 credits (high quality)',
  },
  {
    id: 'hidream',
    title: 'HiDream',
    description:
      'High-quality dream-like imagery with excellent detail and composition.',
    users: '2 credits per image',
  },
  // WAN 2.2
  {
    id: 'wan-2.2',
    title: 'WAN 2.2',
    description: 'Exceptional prompt adherence and ultra realisic textures.',
    users: '2 credits per image',
    icon: WanSvg,
  },
  {
    id: 'wan-2.5-preview',
    title: 'WAN 2.5 Preview',
    description:
      'High-quality image generation with various artistic styles. Support for realistic photography and creative applications.',
    users: '2 credits per image',
    icon: WanSvg,
  },
  {
    id: 'bria-3.2',
    title: 'Bria 3.2',
    description:
      'Trained exclusively on licensed data for safe commercial use. Excels in text rendering and aesthetics.',
    users: '2 credits per image',
    icon: BriaSvg,
  },
  {
    id: 'minimax-image-01',
    title: 'MiniMax Image 01',
    description:
      "MiniMax's latest text-to-image model with excellent prompt following and high-quality visual generation.",
    users: '0.5 credits per image',
    icon: HailuoSvg,
  },
  {
    id: 'seedream-4.0',
    title: 'Seedream 4.0',
    description:
      "ByteDance's advanced text-to-image model with excellent prompt following and visual quality.",
    users: '2 credits per image',
    tags: ['4K'],
    resolutions: ['1K', '2K', '4K'],
    icon: ByteDanceSvg,
  },
  {
    id: 'seedream-3.0',
    title: 'Seedream 3.0',
    description:
      "ByteDance's advanced text-to-image model with excellent prompt following and visual quality.",
    users: '2 credits per image',
    icon: ByteDanceSvg,
  },

  // Dreamina 3.1
  {
    id: 'dreamina-3.1',
    title: 'Dreamina 3.1',
    description:
      'Dreamina 3.1 is a new model that generates high-quality images from text with incredible aesthetics.',
    users: '2 credits per image',
    icon: ByteDanceSvg,
  },
  //Qwen-Image
  {
    id: 'qwen-image',
    title: 'Qwen-Image',
    icon: QwenSvg,
    description:
      'Qwen-Image is a new model that generates high-quality images from text with incredible aesthetics.',
    users: '2 credits per image',
  },
  {
    id: 'ideogram-v4',
    title: 'Ideogram v4',
    icon: IdeogramSvg,
    description:
      "Ideogram's latest model for crisp visuals, accurate text rendering, and polished posters and logos.",
    users: '3 credits per image',
    tags: ['New'],
  },
  {
    id: 'ideogram',
    title: 'Ideogram v3',
    icon: IdeogramSvg,
    description: 'Best for prompt comprehension and text rendering.',
    users: '3 credits per image (standard), 5 credits (high quality)',
  },
  {
    id: 'recraft-v4.1',
    title: 'Recraft v4.1',
    description:
      'Design-first model for brand systems and editorial work, delivering production-ready images with sharp prompt control and clean composition.',
    users: '2 credits per image',
    tags: ['New'],
    icon: RecraftSvg,
  },
  {
    id: 'recraft-v4.1-pro',
    title: 'Recraft v4.1 Pro',
    description:
      'The highest-quality Recraft v4.1 tier for raster images — maximum detail and prompt adherence for premium production assets.',
    users: '10 credits per image',
    tags: ['New', 'Pro'],
    icon: RecraftSvg,
  },
  {
    id: 'recraft-v4.1-vector',
    title: 'Recraft v4.1 Vector',
    description:
      'Turns prompts into fully editable SVGs with structured layers and clean geometry — ideal for logos, icons, and illustration systems.',
    users: '4 credits per image',
    tags: ['New', 'SVG'],
    icon: RecraftSvg,
  },
  {
    id: 'recraft-v4.1-pro-vector',
    title: 'Recraft v4.1 Pro Vector',
    description:
      'The highest-quality Recraft v4.1 tier for editable SVGs — premium logos, icons, and illustration systems with the cleanest geometry.',
    users: '10 credits per image',
    tags: ['New', 'Pro', 'SVG'],
    icon: RecraftSvg,
  },
  {
    id: 'recraft-v3',
    title: 'Recraft v3',
    description: 'Recommended for best results. 2 credits per image.',
    users: '2 credits per image',
    icon: RecraftSvg,
  },
  {
    id: 'flux-1.1-pro-ultra',
    title: 'FLUX 1.1 Pro Ultra',
    description:
      'Latest FLUX model with superior quality and raw mode for photorealistic images.',
    users: '4 credits per image',
    icon: FluxSvg,
  },

  // Midjourney
  {
    id: 'midjourney',
    title: 'Midjourney V6',
    description: 'Best for high-quality images. 2 credits per image.',
    users: '2 credits per image',
    icon: MidjourneySvg,
  },
  {
    id: 'midjourney-v7',
    title: 'Midjourney V7',
    description: 'Best for high-quality images. 2 credits per image.',
    users: '2 credits per image',
    icon: MidjourneySvg,
  },
  {
    id: 'flux-1',
    title: 'FLUX.1',
    description:
      'State-of-the-art image generation with top of the line prompt following, visual quality, image detail and output diversity.',
    users: '0.25 credits per image',
    icon: FluxSvg,
  },
  {
    id: 'flux-1.1-pro',
    title: 'FLUX 1.1 Pro',
    description:
      'State-of-the-art image generation with superior prompt following, visual quality, and diversity, delivering better quality results faster.',
    users: '2 credits per image',
    icon: FluxSvg,
  },

  {
    id: 'stable-diffusion-3.5',
    title: 'Stable Diffusion 3.5',
    description:
      'Latest Stable Diffusion model with improved quality and speed',
    users: '2 credits per image',
    icon: StableAISvg,
  },
  {
    id: 'stable-diffusion-3.0',
    title: 'Stable Diffusion 3.0',
    description: '2 credits per image',
    users: '2 credits per image',
    icon: StableAISvg,
  },
  {
    id: 'stable-diffusion-xl',
    title: 'Stable Diffusion XL',
    description: '', //'Free model',
    users: '0.25 credits per image',
    icon: StableAISvg,
  },
  // {
  //   id: 'kandinsky-3.0',
  //   title: 'Kandinsky 3.0',
  //   description: '',
  //   users: '1 credit per image',
  // },
  // {
  //   id: 'kandinsky-2.2',
  //   title: 'Kandinsky 2.2',
  //   description: '',
  //   users: '1 credit per image',
  // },
  // {
  //   id: 'kandinsky-2',
  //   title: 'Kandinsky 2',
  //   description: '',
  //   users: '1 credit per image',
  // },
];

// Config-driven WaveSpeed text-to-image models (single async pipeline in
// /api/generate-image.ts via submitWaveSpeedImageTask + the
// /api/wavespeed-image-webhook). Sizing modes, aspect lists, and pixel caps
// are all VERIFIED against the live API (submits with bad sizes fail, some
// with undocumented limits — e.g. WAN 2.7 Pro rejects anything above
// ~1440*1440 despite its schema claiming 8192). Pricing is flat per run
// regardless of size.
export interface WaveSpeedT2IModelConfig {
  /** WaveSpeed model path, e.g. 'alibaba/wan-2.7/text-to-image'. */
  path: string;
  credits: number;
  /**
   * How the model takes sizing:
   * - 'aspect_ratio': aspect string in an `aspect_ratio` param
   * - 'size_aspect': aspect string in the `size` param
   * - 'size_pixels': explicit `size: "width*height"` (see longEdge)
   */
  sizing: 'aspect_ratio' | 'size_aspect' | 'size_pixels';
  /** Accepted aspect ratios — also the UI dimension options. */
  aspects: string[];
  /** size_pixels only: verified max long edge for this model. */
  longEdge?: number;
}

export const WAVESPEED_T2I_MODELS: Record<string, WaveSpeedT2IModelConfig> = {
  'Qwen Image Max': {
    path: 'wavespeed-ai/qwen-image-max/text-to-image',
    credits: 4,
    sizing: 'size_pixels',
    aspects: ['1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'],
    longEdge: 1664, // API range [512*512, 1664*1664]
  },
  'Qwen Image 2.0': {
    path: 'wavespeed-ai/qwen-image-2.0/text-to-image',
    credits: 2,
    sizing: 'size_pixels',
    aspects: ['1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'],
    longEdge: 2048,
  },
  'WAN 2.7 Pro': {
    path: 'alibaba/wan-2.7/text-to-image-pro',
    credits: 4,
    sizing: 'size_pixels',
    aspects: ['1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'],
    longEdge: 1440, // fails above ~1440*1440 (verified live)
  },
  'WAN 2.7': {
    path: 'alibaba/wan-2.7/text-to-image',
    credits: 2,
    sizing: 'size_pixels',
    aspects: ['1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'],
    longEdge: 2048,
  },
  'Krea v2 Large': {
    path: 'wavespeed-ai/krea-v2-large/text-to-image',
    credits: 3,
    sizing: 'aspect_ratio',
    aspects: ['1:1', '4:5', '2:3', '3:2', '4:3', '9:16', '16:9'],
  },
  'Krea v2 Medium': {
    path: 'wavespeed-ai/krea-v2-medium/text-to-image',
    credits: 2,
    sizing: 'aspect_ratio',
    aspects: ['1:1', '3:4', '4:3', '9:16', '16:9'],
  },
  'Krea v2 Medium Turbo': {
    path: 'wavespeed-ai/krea-v2-medium-turbo/text-to-image',
    credits: 1,
    sizing: 'aspect_ratio',
    aspects: ['1:1', '4:5', '2:3', '3:2', '4:3', '9:16', '16:9'],
  },
  'Luma Uni v1': {
    path: 'luma/uni-v1/text-to-image',
    credits: 2,
    sizing: 'size_aspect',
    aspects: ['1:1', '2:3', '3:2', '9:16', '16:9', '1:2', '2:1'],
  },
  'Boogu Image': {
    path: 'wavespeed-ai/boogu-image/text-to-image',
    credits: 3,
    sizing: 'aspect_ratio',
    aspects: ['1:1', '3:4', '4:3', '9:16', '16:9'],
  },
  'Cosmos 3 Super': {
    path: 'nvidia/cosmos-3-super/text-to-image',
    credits: 2,
    sizing: 'size_aspect',
    aspects: ['1:1', '3:4', '4:3', '9:16', '16:9'],
  },
  'P-Image': {
    path: 'pruna-ai/p-image/text-to-image',
    credits: 0.5,
    sizing: 'aspect_ratio',
    aspects: ['1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'],
  },
};

/**
 * Display label for an image model. Strips the "OpenAI " prefix since the provider
 * is already shown via the model icon.
 */
export function getModelDisplayName(modelName: string): string {
  return modelName.replace(/^OpenAI /, '');
}

// Dimension options for different models
export interface EditModelOption {
  value: string;
  label: string;
  credits: number;
  resolutions?: string[];
}

// Single source of truth for the "Edit Image with AI" model picker. Consumed by
// the AI image generator (ai-images), the EditModal, the standalone image
// editor, the edit PromptForm, and the public generate-image API (for
// validation). Keep the values in sync with the editModel routing in
// /api/generate-image.ts and /api/images.ts — any value listed here MUST have a
// matching routing branch, otherwise the API would silently fall back.
export const editModels: EditModelOption[] = [
  {
    value: 'Nano Banana 2.1',
    label: 'Nano Banana 2.1',
    credits: 2,
    resolutions: ['1K', '2K', '4K'],
  },
  {
    value: 'Nano Banana 2 Lite',
    label: 'Nano Banana 2 Lite',
    credits: 1,
    resolutions: ['1K'],
  },
  {
    value: 'Nano Banana 2',
    label: 'Nano Banana 2',
    credits: 2,
    resolutions: ['0.5K', '1K', '2K', '4K'],
  },
  {
    value: 'Nano Banana Pro',
    label: 'Nano Banana Pro',
    credits: 4,
    resolutions: ['1K', '2K', '4K'],
  },
  {
    value: 'Grok',
    label: 'Grok',
    credits: 1,
    resolutions: ['1K', '2K'],
  },
  {
    value: 'Grok Pro',
    label: 'Grok Pro',
    credits: 4,
    resolutions: ['1K', '2K'],
  },
  {
    value: 'Grok Imagine 2.0',
    label: 'Grok Imagine 2.0',
    credits: 4,
    resolutions: ['1K', '2K'],
  },
  {
    value: 'Muse Image',
    label: 'Muse Image',
    credits: 1,
  },
  { value: 'OpenAI GPT Image 1.5', label: 'GPT Image 1.5', credits: 8 },
  {
    value: 'OpenAI GPT Image 2 Low',
    label: 'GPT Image 2 Low',
    credits: 0.5,
  },
  {
    value: 'OpenAI GPT Image 2 Medium',
    label: 'GPT Image 2 Medium',
    credits: 3,
  },
  {
    value: 'OpenAI GPT Image 2 High',
    label: 'GPT Image 2 High',
    credits: 11,
  },
  {
    value: 'OpenAI GPT Image 2.5 Flare Low',
    label: 'GPT Image 2.5 Flare Low',
    credits: 0.5,
  },
  {
    value: 'OpenAI GPT Image 2.5 Flare Medium',
    label: 'GPT Image 2.5 Flare Medium',
    credits: 3,
  },
  {
    value: 'OpenAI GPT Image 2.5 Flare High',
    label: 'GPT Image 2.5 Flare High',
    credits: 11,
  },
  {
    value: 'OpenAI GPT Image 2.5 Sunburst Medium',
    label: 'GPT Image 2.5 Sunburst Medium',
    credits: 3,
  },
  {
    value: 'OpenAI GPT Image 2.5 Sunburst High',
    label: 'GPT Image 2.5 Sunburst High',
    credits: 11,
  },
  {
    value: 'Seedream 4.5',
    label: 'Seedream 4.5',
    credits: 2,
    resolutions: ['1K', '2K', '4K'],
  },
  {
    value: 'Seedream 5.0 Lite',
    label: 'Seedream 5.0 Lite',
    credits: 2,
    resolutions: ['1K', '2K', '3K'],
  },
  {
    value: 'Seedream 5.0 Pro',
    label: 'Seedream 5.0 Pro',
    credits: 8,
    resolutions: ['1K', '2K'],
  },
  {
    value: 'FLUX.2 [max]',
    label: 'FLUX.2 [max]',
    credits: 2.5,
    resolutions: ['1K', '2K'],
  },
  {
    value: 'FLUX.2 [pro]',
    label: 'FLUX.2 [pro]',
    credits: 2,
    resolutions: ['1K', '2K'],
  },
  {
    value: 'FLUX.2 [flex]',
    label: 'FLUX.2 [flex]',
    credits: 3,
    resolutions: ['1K', '2K'],
  },
  {
    value: 'Seedream 4.0',
    label: 'Seedream 4.0',
    credits: 2,
    resolutions: ['1K', '2K', '4K'],
  },
  { value: 'Nano Banana', label: 'Nano Banana', credits: 2 },
  { value: 'Qwen Image', label: 'Qwen Image', credits: 2 },
  { value: 'Reve', label: 'Reve', credits: 2 },
  { value: 'Reve Fast', label: 'Reve Fast', credits: 0.5 },
  { value: 'Mai Image 2.5', label: 'Mai Image 2.5', credits: 2 },
  { value: 'Flux Kontext Pro', label: 'Flux Kontext Pro', credits: 2 },
  { value: 'Flux Kontext Max', label: 'Flux Kontext Max', credits: 4 },
  { value: 'OpenAI GPT Image 1', label: 'GPT Image 1', credits: 8 },
  {
    value: 'Ideogram v3 Character',
    label: 'Ideogram v3 Character',
    credits: 5,
  },
];

// Convenience list of just the editModel identifiers, for validation.
export const EDIT_MODEL_VALUES = editModels.map((m) => m.value);

// Edit models above this base credit cost are premium — locked for Free plan
// users (mirrors the freeUserAccess gating on video models). Checked both in
// the model pickers (EditModelSelector, edit PromptForm) and server-side in
// /api/images.ts + /api/generate-image.ts.
export const PREMIUM_EDIT_MODEL_CREDITS_THRESHOLD = 3;

export function isPremiumEditModel(editModel: string): boolean {
  const model = editModels.find((m) => m.value === editModel);
  return !!model && model.credits > PREMIUM_EDIT_MODEL_CREDITS_THRESHOLD;
}

// Text-to-image models whose MINIMUM per-image cost exceeds the premium
// threshold (they can't be generated at ≤3 credits with any settings) —
// locked for Free plan users in the ModelSelectorModal and enforced
// server-side in /api/generate-image. Models with a cheap base tier
// (e.g. GPT Image 1.5 at 2 credits standard, FLUX.2 [max] at 2.5 @1K)
// deliberately stay available.
export const PREMIUM_IMAGE_MODELS = [
  'Nano Banana Pro',
  'Grok Pro',
  'OpenAI GPT Image 2 High',
  'OpenAI GPT Image 2.5 Flare High',
  'OpenAI GPT Image 2.5 Sunburst High',
  'Seedream 5.0 Pro',
  'Qwen Image Max',
  'WAN 2.7 Pro',
  'HunyuanImage 3.0',
  'Flux Kontext Max',
  'Recraft v4.1 Pro',
  'Recraft v4.1 Vector',
  'Recraft v4.1 Pro Vector',
  'FLUX 1.1 Pro Ultra',
];

export function isPremiumImageModel(model: string): boolean {
  return PREMIUM_IMAGE_MODELS.includes(model);
}

export const fluxDimensionsOptions = [
  {
    value: '1:1',
    label: 'Square (1:1)',
  },
  {
    value: '21:9',
    label: 'Ultrawide (21:9)',
  },
  {
    value: '16:9',
    label: 'Landscape (16:9)',
  },
  {
    value: '4:3',
    label: 'Landscape (4:3)',
  },
  {
    value: '3:2',
    label: 'Landscape (3:2)',
  },
  {
    value: '2:3',
    label: 'Portrait (2:3)',
  },
  {
    value: '3:4',
    label: 'Portrait (3:4)',
  },
  {
    value: '9:16',
    label: 'Portrait (9:16)',
  },
  {
    value: '9:21',
    label: 'Portrait Ultrawide (9:21)',
  },
];

interface GetDimensionsOptions {
  model: ImageModelType | string;
  action?: string;
  editModel?: string;
  newBeforeImages?: string[];
  selectedModels?: ImageModelType[];
}

/**
 * Get dimension options for a given image model and context
 * @param options - Configuration object with model, action, editModel, newBeforeImages, and selectedModels
 * @returns Array of dimension strings (e.g., ['1:1', '16:9'] or ['1024x1024', '1792x1024'])
 */
export function getDimensionsForModel(options: GetDimensionsOptions): string[] {
  const {
    model,
    action,
    editModel,
    newBeforeImages = [],
    selectedModels = [],
  } = options;

  let dimensionsOptions: any;

  // Check if custom model is selected
  const isCustomModelSelected =
    typeof model === 'string' && !models.includes(model as ImageModelType);

  // Base model dimensions
  if (WAVESPEED_T2I_MODELS[model as string]) {
    dimensionsOptions = WAVESPEED_T2I_MODELS[model as string].aspects;
  } else if (
    model === 'Midjourney V6' ||
    model === 'Midjourney V7' ||
    model === 'FLUX.1' ||
    model === 'FLUX 1.1 Pro' ||
    model === 'FLUX 1.1 Pro Ultra'
  ) {
    dimensionsOptions = ['1:1', '2:3', '3:2', '4:5', '5:4', '16:9', '9:16'];
  } else if (
    model === 'OpenAI GPT Image 1' ||
    model === 'OpenAI GPT Image 1.5'
  ) {
    dimensionsOptions = ['1024x1024', '1536x1024', '1024x1536'];
  } else if (isGptImage2FamilyModel(model)) {
    dimensionsOptions = GPT_IMAGE_2_ASPECTS;
  } else if (model === 'Nano Banana') {
    dimensionsOptions = [
      '1:1',
      '2:3',
      '3:2',
      '3:4',
      '4:3',
      '4:5',
      '5:4',
      '9:16',
      '16:9',
      '21:9',
    ];
  } else if (
    model === 'Nano Banana 2' ||
    model === 'Nano Banana 2.1' ||
    model === 'Nano Banana 2 Lite'
  ) {
    dimensionsOptions = [
      '1:1',
      '2:3',
      '3:2',
      '3:4',
      '4:3',
      '4:5',
      '5:4',
      '9:16',
      '16:9',
      '21:9',
      '1:4',
      '4:1',
      '1:8',
      '8:1',
    ];
  } else if (model === 'Nano Banana Pro' || model === 'Nano Banana Flash') {
    dimensionsOptions = [
      '1:1',
      '2:3',
      '3:2',
      '3:4',
      '4:3',
      '4:5',
      '5:4',
      '9:16',
      '16:9',
      '21:9',
    ];
  } else if (model === 'REVE' || model === 'Mai Image 2.5') {
    dimensionsOptions = ['1:1', '2:3', '3:2', '3:4', '4:3', '9:16', '16:9'];
  } else if (model === 'Bria 3.2') {
    dimensionsOptions = [
      '1:1',
      '2:3',
      '3:2',
      '3:4',
      '4:3',
      '4:5',
      '5:4',
      '9:16',
      '16:9',
    ];
  } else if (
    model === 'Ideogram v3' ||
    model === 'Ideogram v4' ||
    model === 'Seedream 3.0' ||
    model === 'Seedream 4.0' ||
    model === 'Seedream 4.5' ||
    model === 'Seedream 5.0 Lite' ||
    model === 'Seedream 5.0 Pro' ||
    model === 'MiniMax Image 01' ||
    model === 'HunyuanImage 3.0' ||
    model === 'Flux.1 Krea' ||
    model === 'HiDream' ||
    model === 'WAN 2.2' ||
    model === 'WAN 2.5 Preview' ||
    model === 'Dreamina 3.1' ||
    model === 'Qwen-Image' ||
    model === 'ImagineArt 1.5' ||
    model === 'Recraft v4.1' ||
    model === 'Recraft v4.1 Vector' ||
    model === 'Recraft v4.1 Pro' ||
    model === 'Recraft v4.1 Pro Vector' ||
    isCustomModelSelected
  ) {
    dimensionsOptions = ['1:1', '3:4', '4:3', '9:16', '16:9'];
  } else if (
    model === 'FLUX.2 [max]' ||
    model === 'FLUX.2 [flex]' ||
    model === 'FLUX.2 [pro]' ||
    model === 'FLUX.2 [dev]'
  ) {
    dimensionsOptions = ['1:1', '2:3', '3:2', '4:5', '5:4', '16:9', '9:16'];
  } else if (
    model === 'Grok' ||
    model === 'Grok Pro' ||
    model === 'Grok Imagine 2.0' ||
    // Muse Image's `size` sets an aspect ratio (any ratio up to 16:1); we
    // expose the same set as Grok.
    model === 'Muse Image'
  ) {
    dimensionsOptions = [
      '1:1',
      '3:4',
      '4:3',
      '9:16',
      '16:9',
      '2:3',
      '3:2',
      '1:2',
      '2:1',
    ];
  } else if (model === 'Z-Image Turbo') {
    dimensionsOptions = ['1:1', '4:3', '3:4', '16:9', '9:16'];
  } else if (model === 'Kandinsky 3.0') {
    dimensionsOptions = ['1024x1024'];
  } else if (
    model === 'Flux Kontext Pro' ||
    model === 'Flux Kontext Max' ||
    model === 'Stable Diffusion 3.0' ||
    model === 'Stable Diffusion 3.5'
  ) {
    dimensionsOptions = fluxDimensionsOptions.map((d) => d.value);
  } else if (model === 'Recraft v3') {
    dimensionsOptions = [
      '1024x1024',
      '1365x1024',
      '1024x1365',
      '1536x1024',
      '1024x1536',
      '1820x1024',
      '1024x1820',
      '1024x2048',
      '2048x1024',
      '1434x1024',
      '1024x1434',
      '1024x1280',
      '1280x1024',
      '1024x1707',
      '1707x1024',
    ];
  } else {
    dimensionsOptions = ['1024x1024', '2048x1536', '1536x2048'];
  }

  // Override for specific actions
  if (
    (action === 'Caricature' || action === 'Muppets') &&
    newBeforeImages.length > 0
  ) {
    dimensionsOptions = ['1024x1024', '1536x1024', '1024x1536'];
  }

  // Override for edit image with AI
  if (action === 'Edit Image with AI' && newBeforeImages.length > 0) {
    if (editModel === 'Nano Banana') {
      dimensionsOptions = [
        'auto',
        '1:1',
        '2:3',
        '3:2',
        '3:4',
        '4:3',
        '4:5',
        '5:4',
        '9:16',
        '16:9',
        '21:9',
      ];
    } else if (
      editModel === 'Nano Banana 2' ||
      editModel === 'Nano Banana 2.1' ||
      editModel === 'Nano Banana 2 Lite'
    ) {
      dimensionsOptions = [
        'auto',
        '1:1',
        '2:3',
        '3:2',
        '3:4',
        '4:3',
        '4:5',
        '5:4',
        '9:16',
        '16:9',
        '21:9',
        '1:4',
        '4:1',
        '1:8',
        '8:1',
      ];
    } else if (
      editModel === 'Nano Banana Pro' ||
      editModel === 'Nano Banana Flash'
    ) {
      dimensionsOptions = [
        'auto',
        '1:1',
        '2:3',
        '3:2',
        '3:4',
        '4:3',
        '4:5',
        '5:4',
        '9:16',
        '16:9',
        '21:9',
      ];
    } else if (editModel === 'Qwen Image') {
      dimensionsOptions = ['1:1', '3:4', '4:3', '9:16', '16:9'];
    } else if (
      editModel === 'FLUX.2 [max]' ||
      editModel === 'FLUX.2 [pro]' ||
      editModel === 'FLUX.2 [flex]' ||
      editModel === 'Seedream 4.5' ||
      editModel === 'Seedream 5.0 Lite' ||
      editModel === 'Seedream 5.0 Pro' ||
      editModel === 'Seedream 4.0'
    ) {
      dimensionsOptions = ['auto', '1:1', '3:4', '4:3', '9:16', '16:9'];
    } else if (editModel === 'Ideogram v3 Character') {
      dimensionsOptions = ['auto', '1:1', '3:4', '4:3', '9:16', '16:9'];
    } else if (
      editModel === 'Grok' ||
      editModel === 'Grok Pro' ||
      editModel === 'Grok Imagine 2.0' ||
      editModel === 'Muse Image'
    ) {
      dimensionsOptions = [
        'auto',
        '1:1',
        '3:4',
        '4:3',
        '9:16',
        '16:9',
        '2:3',
        '3:2',
      ];
    } else if (editModel === 'Reve' || editModel === 'Reve Fast') {
      dimensionsOptions = ['auto'];
    } else if (editModel === 'Mai Image 2.5') {
      dimensionsOptions = [
        'auto',
        '1:1',
        '2:3',
        '3:2',
        '3:4',
        '4:3',
        '9:16',
        '16:9',
      ];
    } else if (isGptImage2FamilyModel(editModel)) {
      dimensionsOptions = GPT_IMAGE_2_EDIT_ASPECTS;
    } else if (
      newBeforeImages.length > 2 ||
      editModel === 'OpenAI GPT Image 1' ||
      editModel === 'OpenAI GPT Image 1.5'
    ) {
      dimensionsOptions = ['auto', '1024x1024', '1536x1024', '1024x1536'];
    } else {
      dimensionsOptions = [
        'auto',
        ...fluxDimensionsOptions.map((d) => d.value),
      ];
    }
  }

  // Override for multiple selected models
  if (selectedModels.length > 1) {
    dimensionsOptions = ['1:1'];
  }

  return dimensionsOptions;
}
